import axios from 'axios';
import { apiClient } from './client';
import type { ApiResponse, PaginatedResponse } from './client';

// Use relative URL in production (same server), absolute in development
const API_URL = import.meta.env.VITE_API_URL || (import.meta.env.PROD ? '/api' : 'http://localhost:8080/api');

export interface Build {
  id: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'EXPIRED';
  filename: string;
  engineType: string;
  engineVersion?: string;
  watermark?: string;
  zipPassword?: string;
  error?: string;
  consoleOutput?: string;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  expiresAt?: string;
  downloadUrl?: string;
}

export interface UploadResult {
  uploadId: string;
  filename: string;
  size: number;
  expiresAt: string;
}

export interface Engine {
  type: string;
  version: string;
  name: string;
  supportedExtensions: string[];
  isDefault: boolean;
}

export interface VMProtectSettings {
  packOutput?: boolean;
  importProtection?: boolean;
  memoryProtection?: boolean;
  resourceProtection?: boolean;
  debuggerUsermode?: boolean;
  debuggerKernelmode?: boolean;
  vmDetection?: boolean;
  stripRelocations?: boolean;
  stripDebug?: boolean;
  shadowStack?: boolean;
  randomizeTimestamp?: boolean;  // Randomize PE header TimeDateStamp
  vmComplexity?: number;
  vmVersion?: number;  // 0 = Default, 1 = Compact
  vmInstances?: number; // 1-10
  vmSectionName?: string;
  hwid?: string;
  useWatermark?: boolean;
  customWatermark?: string;
  procedures?: Array<{
    mapAddress: string;
    compilationType: number;
    complexity?: number;
    lockToSerial?: boolean;
    includedInCompilation?: boolean;
  }>;
  segmentExclusions?: Array<{
    name: string;
    excludeFromPacking?: boolean;
    excludeFromProtection?: boolean;
  }>;
  messages?: {
    debugger?: string;
    virtualTools?: string;
    corrupted?: string;
    serialRequired?: string;
    hwidMismatch?: string;
  };
}

// PE Analysis Types
export interface PESection {
  name: string;
  virtualSize: number;
  virtualAddress: number;
  rawSize: number;
  rawAddress: number;
  characteristics: number;
  flags: string[];
}

export interface PEImportEntry {
  dllName: string;
  functions: Array<{
    name: string;
    ordinal?: number;
    hint?: number;
  }>;
}

export interface PEExportEntry {
  name: string;
  ordinal: number;
  address: number;
}

export interface PEHeaderInfo {
  dosSignature: string;
  machine: string;
  machineRaw: number;
  numberOfSections: number;
  timestamp: string;
  characteristics: number;
  characteristicFlags: string[];
  magic: string;
  entryPoint: number;
  imageBase: number;
  sectionAlignment: number;
  fileAlignment: number;
  subsystem: string;
  subsystemRaw: number;
  dllCharacteristics: number;
  dllCharacteristicFlags: string[];
  sizeOfImage: number;
  sizeOfHeaders: number;
  checksum: number;
}

export interface SignatureInfo {
  signer: string;
  issuer: string;
  serialNumber: string;
  validFrom: string;
  validTo: string;
}

export interface PEAnalysis {
  filename: string;
  fileSize: number;
  sha1: string;
  isPE: boolean;
  is64Bit: boolean;
  isDLL: boolean;
  isSigned: boolean;
  signatureInfo?: SignatureInfo;
  header: PEHeaderInfo;
  sections: PESection[];
  imports: PEImportEntry[];
  exports: PEExportEntry[];
  hasResources: boolean;
  hasDebugInfo: boolean;
  hasRelocations: boolean;
}

// PDB Types
export interface PdbFunction {
  name: string;
  demangledName?: string;
  rva: number;
  segment: number;
  size?: number;
  isCode: boolean;
  isPublic: boolean;
}

export interface PdbUploadResult {
  uploadId: string;
  filename: string;
  size: number;
  functionsCount: number;
  parseTimeMs: number;
}

export interface DisassemblyLine {
  address: string;
  bytes: string;
  mnemonic: string;
  operands: string;
  text: string;
}

export interface DisassemblyResult {
  functionName: string;
  startAddress: number;
  size: number;
  instructions: DisassemblyLine[];
  is64Bit: boolean;
}

export interface PdbStats {
  totalFunctions: number;
  codeFunctions: number;
  publicFunctions: number;
  sections: number;
  machine?: number;
  age?: number;
  parseTimeMs: number;
}

// VMP Config Types
export interface VmpProcedure {
  mapAddress: string;
  options: number;
  compilationType: number;
  complexity?: number;
  lockToSerial?: boolean;
}

// VM Version constants
export const VM_VERSION = {
  DEFAULT: 0,
  COMPACT: 1,
} as const;

export type VmVersionType = typeof VM_VERSION[keyof typeof VM_VERSION];

export interface VmpConfigSettings {
  packOutputFile: boolean;
  importProtection: boolean;
  resourceProtection: boolean;
  stripDebugInfo: boolean;
  stripRelocations: boolean;
  debugMode: boolean;
  memoryProtection: boolean;
  virtualizationTools: boolean;
  checkDebugger: boolean;
  checkKernelDebugger: boolean;
  checkVirtualMachine: boolean;
  vmCodeSectionName?: string;
  vmComplexity: number;
  vmVersion: VmVersionType;  // 0 = Default, 1 = Compact
  procedures: Array<{
    mapAddress: string;
    compilationType: number;
    complexity?: number;
    lockToSerial: boolean;
    includedInCompilation: boolean;
  }>;
  messages?: {
    debugger?: string;
    virtualTools?: string;
    corrupted?: string;
    serialRequired?: string;
    hwidMismatch?: string;
  };
}

export interface VmpConfigRaw {
  inputFileName?: string;
  outputFileName?: string;
  options: number;
  vmCodeSectionName?: string;
  vmComplexity: number;
  vmVersion?: VmVersionType;  // 0 = Default, 1 = Compact
  vmInstances?: number;
  hwid?: string;
  procedures: VmpProcedure[];
  messages?: VmpConfigSettings['messages'];
  excludedSegments?: string[];
}

export interface VmpConfigParseResult {
  raw: VmpConfigRaw;
  settings: VmpConfigSettings;
}

export const buildsApi = {
  upload: async (file: File): Promise<UploadResult> => {
    const formData = new FormData();
    formData.append('file', file);
    
    // Use axios directly for file uploads to avoid default Content-Type header issues
    const token = localStorage.getItem('accessToken');
    const response = await axios.post<ApiResponse<UploadResult>>(`${API_URL}/builds/upload`, formData, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
    });
    return response.data.data!;
  },

  start: async (
    options: {
      uploadId?: string;
      storedFileId?: string;
      settings?: VMProtectSettings;
      engineType?: string;
      engineVersion?: string;
      zipPassword?: boolean;
    }
  ): Promise<Build> => {
    const response = await apiClient.post<ApiResponse<Build>>('/builds/start', {
      uploadId: options.uploadId,
      storedFileId: options.storedFileId,
      engineType: options.engineType || 'vmprotect',
      engineVersion: options.engineVersion,
      settings: options.settings,
      zipPassword: options.zipPassword,
    });
    return response.data.data!;
  },

  getStatus: async (buildId: string): Promise<Build> => {
    const response = await apiClient.get<ApiResponse<Build>>(`/builds/${buildId}/status`);
    return response.data.data!;
  },

  list: async (page = 1, limit = 20): Promise<PaginatedResponse<Build>> => {
    const response = await apiClient.get<PaginatedResponse<Build>>('/builds', {
      params: { page, limit },
    });
    return response.data;
  },

  delete: async (buildId: string): Promise<void> => {
    await apiClient.delete(`/builds/${buildId}`);
  },

  getEngines: async (): Promise<Engine[]> => {
    const response = await apiClient.get<ApiResponse<Engine[]>>('/builds/engines');
    return response.data.data!;
  },

  download: (buildId: string): string => {
    const token = localStorage.getItem('accessToken');
    return `${apiClient.defaults.baseURL}/builds/${buildId}/download?token=${token}`;
  },

  analyze: async (uploadId: string): Promise<PEAnalysis> => {
    const response = await apiClient.get<ApiResponse<PEAnalysis>>(`/builds/${uploadId}/analyze`);
    return response.data.data!;
  },

  // PDB Methods
  uploadPdb: async (uploadId: string, file: File): Promise<PdbUploadResult> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const token = localStorage.getItem('accessToken');
    const response = await axios.post<ApiResponse<PdbUploadResult>>(`${API_URL}/pdb/${uploadId}/pdb`, formData, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
    });
    return response.data.data!;
  },

  getFunctions: async (
    uploadId: string,
    options?: {
      search?: string;
      page?: number;
      limit?: number;
      sortBy?: 'name' | 'address';
    }
  ): Promise<{ data: PdbFunction[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> => {
    const response = await apiClient.get<{
      success: boolean;
      data: PdbFunction[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }>(`/pdb/${uploadId}/functions`, {
      params: {
        search: options?.search,
        page: options?.page || 1,
        limit: options?.limit || 100,
        sortBy: options?.sortBy || 'address',
      },
    });
    return { data: response.data.data, pagination: response.data.pagination };
  },

  searchFunctions: async (uploadId: string, query: string, limit = 20): Promise<PdbFunction[]> => {
    const response = await apiClient.get<ApiResponse<PdbFunction[]>>(`/pdb/${uploadId}/functions/search`, {
      params: { q: query, limit },
    });
    return response.data.data!;
  },

  disassemble: async (
    binaryUploadId: string,
    rva: number,
    size?: number,
    pdbUploadId?: string
  ): Promise<DisassemblyResult> => {
    const response = await apiClient.post<ApiResponse<DisassemblyResult>>(`/pdb/${binaryUploadId}/disassemble`, {
      rva,
      size,
      pdbUploadId,
    });
    return response.data.data!;
  },

  disassemblePreview: async (uploadId: string, rva: number, maxInstructions = 20): Promise<DisassemblyLine[]> => {
    const response = await apiClient.get<ApiResponse<DisassemblyLine[]>>(`/pdb/${uploadId}/disassemble/preview`, {
      params: { rva, max: maxInstructions },
    });
    return response.data.data!;
  },

  getPdbStats: async (uploadId: string): Promise<PdbStats> => {
    const response = await apiClient.get<ApiResponse<PdbStats>>(`/pdb/${uploadId}/pdb/stats`);
    return response.data.data!;
  },

  // VMP Config Parser
  parseVmpConfig: async (file: File): Promise<VmpConfigParseResult> => {
    const formData = new FormData();
    formData.append('vmpFile', file);
    
    const token = localStorage.getItem('accessToken');
    const response = await axios.post<ApiResponse<VmpConfigParseResult>>(`${API_URL}/vmp/parse`, formData, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
    });
    return response.data.data!;
  },
};
